import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { BellRing, Copy, KeyRound, Smartphone } from "lucide-react";

// Superadmin: notificacoes do app (credencial do Firebase) e publicacao
// automatica de novas versoes do APK (token usado pelo GitHub Actions).
export default function AppAndroidAdmin() {
  const [fb, setFb] = useState(null);
  const [saJson, setSaJson] = useState("");
  const [saving, setSaving] = useState(false);
  const [rel, setRel] = useState(null);
  const [newToken, setNewToken] = useState("");

  const load = async () => {
    try {
      const [{ data: f }, { data: r }] = await Promise.all([
        api.get("/admin/firebase"),
        api.get("/admin/mobile-release"),
      ]);
      setFb(f);
      setRel(r);
    } catch {
      // backend ainda sem essas rotas: o card fica oculto sem quebrar a tela
      setFb(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveFirebase = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/firebase", { service_account_json: saJson.trim() });
      toast.success(data.configured ? `Firebase configurado (${data.project_id})` : "Credencial removida");
      setSaJson("");
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSaving(false);
    }
  };

  const genToken = async () => {
    if (rel?.has_token && !window.confirm("Gerar um novo token invalida o anterior. Será preciso atualizar o secret no GitHub. Continuar?")) return;
    try {
      const { data } = await api.post("/admin/mobile-release/token");
      setNewToken(data.token);
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  if (fb === false || fb === null) return null;

  return (
    <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-5" data-testid="app-android-admin-card">
      <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
        <Smartphone className="w-4 h-4 text-emerald-400" />
        <div className="font-outfit text-white font-semibold">App Android — notificações e atualizações</div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-white">
          <BellRing className="w-4 h-4 text-amber-300" /> Notificações (Firebase)
          <span className={`ml-auto text-xs ${fb.configured ? "text-emerald-300" : "text-amber-300"}`}>
            {fb.configured ? `Configurado · ${fb.project_id} · ${fb.devices} celular(es)` : "Não configurado"}
          </span>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          No console do Firebase: ⚙ Configurações do projeto → Contas de serviço → <b>Gerar nova chave privada</b>.
          Abra o arquivo baixado, copie todo o conteúdo e cole abaixo. A chave fica guardada só no servidor e não é exibida de novo.
        </p>
        <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">JSON da conta de serviço</Label>
        <Textarea
          data-testid="firebase-sa-input"
          rows={4}
          value={saJson}
          onChange={(e) => setSaJson(e.target.value)}
          placeholder='{"type": "service_account", "project_id": "...", ...}'
          className="bg-slate-950 border-slate-800 text-white font-mono text-xs"
        />
        <div className="flex justify-end">
          <Button data-testid="firebase-save-btn" onClick={saveFirebase} disabled={saving || !saJson.trim()}
            className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
            Salvar credencial
          </Button>
        </div>
      </div>

      <div className="space-y-2 border-t border-slate-800 pt-4">
        <div className="flex items-center gap-2 text-sm text-white">
          <KeyRound className="w-4 h-4 text-sky-300" /> Atualização automática do app
          <span className="ml-auto text-xs text-slate-400">
            {rel?.version_name ? `Versão publicada: ${rel.version_name}` : "Nenhuma versão publicada"}
          </span>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          O GitHub publica cada versão nova do APK aqui, e o app avisa os usuários para atualizar.
          Gere o token e cadastre-o no GitHub como secret <span className="font-mono text-slate-300">COBRAZAP_RELEASE_TOKEN</span>.
        </p>
        {newToken && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 space-y-2">
            <div className="text-xs text-amber-200">Copie agora — este token não será mostrado de novo:</div>
            <div className="flex gap-2 items-center">
              <code className="flex-1 text-xs text-white break-all">{newToken}</code>
              <Button size="sm" variant="outline" className="border-slate-700"
                onClick={() => { navigator.clipboard?.writeText(newToken); toast.success("Token copiado"); }}>
                <Copy className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}
        <div className="flex justify-end">
          <Button data-testid="release-token-btn" variant="outline" onClick={genToken} className="border-slate-700 hover:bg-slate-800 hover:text-white">
            {rel?.has_token ? "Gerar novo token" : "Gerar token"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
